<template>
  <div class="table" :class="[tableStatusClass, { selected: selected }]">
    <a class="table-container" @click="$emit('click')" @contextmenu.prevent="onRightClick">
      <div v-if="table.status_id !== 1" class="table-length">
        <span class="badge ts-font-semibold" :class="false ? 'bg-overtime' : ''">
          <span>{{ minutesUsed }}</span>
        </span>
      </div>
      <div class="table-status"></div>
      <div class="table-name">
        <div class="name">{{ $t('restaurantSale.table') }}</div>
        <div class="no">{{ table.table_no }}</div>
        <div class="countdown">
          <span v-if="table.status_id === 3" class="countdown-timer">
            <span>{{ minutesLeft }}</span>
          </span>
          <span v-else class="status-text">
            {{ getStatusText() }}
          </span>
        </div>
      </div>
      <div class="table-info-row">
        <div class="table-info-col">
          <div class="table-info-container">
            <span class="icon">
              <span v-show="table.status_id != 1">🕐</span>
            </span>
            <span class="text">{{ table.start_time }}</span>
          </div>
        </div>
        <div class="table-info-col">
          <div class="table-info-container">
            <span class="icon">
              <span v-show="table.status_id != 1 && hasTimeLimit">⏳</span>
            </span>
            <span class="text">{{ endTime }}</span>
          </div>
        </div>
      </div>
      <div class="table-info-row">
        <div class="table-info-col">
          <div class="table-info-container">
            <span class="icon">
              <span v-show="table.status_id != 1">💰</span>
            </span>
            <span class="text">{{ defaultCurrency }}
              {{ table.invoice_amount | format_number }}</span>
          </div>
        </div>
        <div class="table-info-col">
          <div class="table-info-container">
            <span class="icon">
              <span v-show="table.status_id != 1">💵</span>
            </span>
            <span class="text">{{ table.invoice_status }}</span>
          </div>
        </div>
      </div>
    </a>
    <div v-if="showMenu" class="context-menu" :style="{ top: menuY + 'px', left: menuX + 'px' }">
      <List size="small">
        <ListItem>
          <a @click="onNomalCheckout">
            <span class="ts-space-x-1">
              <i class="fa fa-calendar-check"></i>
              <span>{{ $t('restaurantSale.nomarlCheckIn') }}</span>
            </span>
          </a>
        </ListItem>
        <ListItem v-if="table.guest_level_id == 1">
          <a @click="onMemeberCheckout">
            <span class="ts-space-x-1">
              <i class="fa fa-id-card"></i>
              <span>{{ $t('restaurantSale.memberCheckIn') }}</span>
            </span>
          </a>
        </ListItem>
      </List>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'TableCard',
  props: {
    table: {
      type: Object,
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    },
    defaultCurrency: {
      type: String,
      default: ''
    }
  },
  filters: {
    format_number(value) {
      if (value) {
        return `${parseFloat(value).toLocaleString()}`
      }
      return 0
    }
  },
  computed: {
    hasTimeLimit() {
      return this.getMinEndTime() !== null;
    },

    tableStatusClass() {
      // Check for over time alert first (assuming status_id 4 or a new field)
      if (this.table.status_id == 4 || this.table.over_time_alert) {
        return 'over-time-alert'
      }

      // Existing status checks
      if (this.table.status_id == 1) {
        return 'available'
      }
      if (this.table.status_id == 2) {
        return 'disabled'
      }
      if (this.table.status_id == 3) {
        // Additional check for over time within in-use status
        if (this.hasTimeLimit && this.isTableOverTime()) {
          return 'over-time-alert'
        }
        return 'in-use'
      }

      return 'available' // fallback
    },

    formattedCountdown() {
      if (this.table.status_id !== 3 || !this.hasTimeLimit) {
        return ''
      }

      const timeLeft = this.calculateTimeLeft()

      if (timeLeft <= 0) {
        return 'OVERTIME'
      }

      const minutes = Math.floor(timeLeft / (60 * 1000))
      const seconds = Math.floor((timeLeft % (60 * 1000)) / 1000)

      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },

    minutesUsed() {
      if (!this.table.start_time) {
        return 'N/A'
      }
      const startTime = this.parseTimeString(this.table.start_time)
      const currentTime = new Date()
      const elapsedTime = currentTime - startTime

      const totalMinutes = Math.ceil(elapsedTime / (60 * 1000)) // Round up minutes
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60

      if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
      }

      return `${minutes}m`
    },

    minutesLeft() {
      if (this.table.status_id !== 3) {
        return ''
      }

      if (this.table.main_linked_key) {
        return 'Linked to: ' + this.table.main_linked_key.table_no
      }

      if (!this.table.start_time) {
        return 'Waiting'
      }

      // Unlimited time case
      if (!this.hasTimeLimit) {
        return 'Unlimited'
      }

      const timeLeft = this.calculateTimeLeft()

      if (timeLeft <= 0) {
        return 'OVERTIME'
      }

      const totalMinutes = Math.ceil(timeLeft / (60 * 1000)) // Round up minutes
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60

      if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
      }

      return `${minutes}m`
    },

    endTime() {
      if (!this.table.start_time) {
        return ''
      }

      if (!this.hasTimeLimit) {
        return 'Unlimited'
      }
      if (!this.table.start_time) {
        return 'Unlimited'
      }

      // Get the max service length from invoice_detail
      const timeLimitInMs = this.getMinEndTime()

      return timeLimitInMs.format("hh:mm A");
    }
  },
  methods: {
    onNomalCheckout() {
      this.showMenu = false;
      this.$emit('click');
    },
    onMemeberCheckout() {
      this.$emit('member-checkout')
      this.showMenu = false;
    },
    onRightClick(e) {
      this.showMenu = false;
      if (this.$el.contains(e.target)) {
        if (this.table.status_id != 1) {
          return;
        }
        const menuWidth = 135; // approximate menu width
        const menuHeight = 74; // approximate menu height

        const { innerWidth, innerHeight } = window;

        let x = e.clientX;
        let y = e.clientY;

        if (x + menuWidth > innerWidth) x = innerWidth - menuWidth - 8;
        if (y + menuHeight > innerHeight) y = innerHeight - menuHeight - 8;

        this.menuX = x;
        this.menuY = y;
        this.showMenu = true;
      }
    },
    closeMenu(e) {
      if (!this.$el.contains(e.target)) {
        this.showMenu = false;
      }
    },
    getMinEndTime() {
      //Main linked table case
      if (this.table.main_linked_key) {
        const table = this.$store.state.restaurantSale.counterCheckout.tables.find(el => el.table_id == this.table.main_linked_key.table_id);

        if (!table.invoice_detail || !Array.isArray(table.invoice_detail)) {
          return null // No time limit
        }

        const minEndTime = table.invoice_detail
          .filter(item => item.start_time != null && item.end_time == null && item.service_length)
          .map(item => moment(item.start_time).add(item.service_length, 'minutes'))
          .reduce((min, current) =>
            min == null || current.isBefore(min) ? current : min, null)

        if (!minEndTime) {
          return null // No time limit
        }

        return minEndTime
      }
      // Find the maximum service_length from invoice_detail items
      if (!this.table.invoice_detail || !Array.isArray(this.table.invoice_detail)) {
        return null // No time limit
      }

      const minEndTime = this.table.invoice_detail
        .filter(item => item.start_time != null && item.end_time == null && item.service_length)
        .map(item => moment(item.start_time).add(item.service_length, 'minutes'))
        .reduce((min, current) =>
          min == null || current.isBefore(min) ? current : min, null)

      if (!minEndTime) {
        return null // No time limit
      }

      return minEndTime
    },

    isTableOverTime() {
      // Check if table has been occupied for more than the max service length
      if (!this.table.start_time || !this.hasTimeLimit) {
        return false
      }

      const now = moment()

      if (!this.table.start_time) {
        return false
      }

      const minEndTime = this.getMinEndTime()

      if (minEndTime) {
        return now.isAfter(minEndTime);
      }

      return false
    },
    calculateTimeLeft() {
      if (!this.table.start_time || !this.hasTimeLimit) {
        return 0
      }

      const now = moment()

      if (!this.table.start_time) {
        return 0
      }

      const minEndTime = this.getMinEndTime()

      if (minEndTime) {
        if (now.isBefore(minEndTime)) {
          return minEndTime.diff(now);
        }
      }
      return 0;
    },

    parseTimeString(timeStr) {
      try {
        // Parse time string like "11:17 AM"
        const today = new Date()
        const [time, period] = timeStr.trim().split(' ')
        const [hours, minutes] = time.split(':')

        let hour24 = parseInt(hours)
        const min = parseInt(minutes)

        // Convert to 24-hour format
        if (period.toUpperCase() === 'PM' && hour24 !== 12) {
          hour24 += 12
        } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
          hour24 = 0
        }

        // Create date object with today's date but specified time
        const parsedTime = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          hour24,
          min,
          0
        )

        // Handle case where start_time might be from yesterday (past midnight scenario)
        const now = new Date()
        if (parsedTime > now) {
          // If parsed time is in the future, assume it was from yesterday
          parsedTime.setDate(parsedTime.getDate() - 1)
        }

        return parsedTime
      } catch (error) {
        console.warn('Failed to parse time string:', timeStr, error)
        return null
      }
    },

    getStatusText() {
      // Return appropriate text for non-active tables
      switch (this.table.status_id) {
        case 1:
          return 'Available'
        case 2:
          return 'Disabled'
        case 4:
          return 'Over Time'
        default:
          return ''
      }
    }
  },

  mounted() {
    document.addEventListener('click', this.closeMenu);
    document.addEventListener('contextmenu', this.onRightClick);

    // Update countdown every second for active tables with time limit
    if (this.table.status_id === 3 && this.hasTimeLimit) {
      this.countdownInterval = setInterval(() => {
        this.$forceUpdate() // Force re-render to update countdown
      }, 1000)
    }
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu);
    document.removeEventListener('contextmenu', this.onRightClick);
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
  },
  data() {
    return {
      countdownInterval: null,
      showMenu: false,
      menuX: 0,
      menuY: 0
    }
  }
}
</script>

<style scoped>
/* Add styles for countdown timer */
.countdown {
  font-weight: bold;
}

.countdown-timer {
  color: #007bff;
  /* Blue color for active countdown */
  font-size: 1.1em;
}

.countdown-timer.overtime {
  color: #dc3545;
  /* Red color for overtime */
  animation: blink 1s infinite;
}

.status-text {
  color: #6c757d;
  /* Gray color for status text */
  font-size: 0.9em;
}

@keyframes blink {

  0%,
  50% {
    opacity: 1;
  }

  51%,
  100% {
    opacity: 0.3;
  }
}

/* Additional styling for over-time-alert tables */
.table.over-time-alert .countdown-timer {
  color: #dc3545;
  animation: blink 1s infinite;
}

.context-menu {
  padding: 6px 0px;
  position: fixed;
  /* so it follows cursor */
  background: white;
  border: 1px solid #ccc;
  z-index: 9999;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .15);
  border-radius: 4px;
}

.ivu-list-item {
  white-space: nowrap;
  padding: 6px 8px;
}

.ivu-list-item:hover {
  background-color: #e2e8f0;
}

.table-length {
  position: absolute;
  top: 10px;
  left: 5px;
  height: 8px;
  display: flex;
  align-items: center;
}

.bg-overtime {
  background: rgba(255, 69, 0, 0.8);
}

.pos-counter .pos-counter-body .pos-counter-content .table-row .table.over-time-alert .table-container:after {
  display: none;
}
</style>